import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePopoverPosition } from "../hooks/usePopoverPosition";
import {
  addCalendarDays,
  addCalendarMonths,
  compareCalendarDates,
  firstWeekdayOfCalendarMonth,
  formatCalendarDate,
  formatCalendarRange,
  isSameCalendarDate,
  isWithinCalendarRange,
  todayCalendarDate,
  type CalendarDate,
  type CalendarDateRange,
} from "../utils/calendarDate";
import "./DateRangePicker.css";

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/** Keep in step with the panel width in the stylesheet; used for viewport clamping. */
const PANEL_WIDTH = 344;

/** First-paint estimate only — the real height is measured once the panel mounts. */
const PANEL_HEIGHT_ESTIMATE = 420;

const pad = (value: number) => String(value).padStart(2, "0");

const dayKey = (date: CalendarDate) =>
  `${date.year}-${pad(date.month)}-${pad(date.day)}`;

/**
 * Ranges a coach reaches for over and over when filing contact activity. Offered because the
 * common case — "last month" — otherwise costs two month-navigations and two clicks every
 * time the report is filed.
 */
const PRESETS: { label: string; build: (today: CalendarDate) => CalendarDateRange }[] = [
  {
    label: "This month",
    build: (today) => ({
      start: { ...today, day: 1 },
      end: today,
    }),
  },
  {
    label: "Last month",
    build: (today) => {
      const lastMonth = addCalendarMonths({ ...today, day: 1 }, -1);
      return {
        start: lastMonth,
        // Day 0 of the following month is the last day of this one.
        end: addCalendarDays(addCalendarMonths(lastMonth, 1), -1),
      };
    },
  },
  {
    label: "Last 30 days",
    build: (today) => ({ start: addCalendarDays(today, -29), end: today }),
  },
  {
    label: "Last 90 days",
    build: (today) => ({ start: addCalendarDays(today, -89), end: today }),
  },
];

export interface DateRangePickerProps {
  /** Visible field label. Also names the panel for assistive tech. */
  label: string;
  value: CalendarDateRange;
  onChange: (range: CalendarDateRange) => void;
  id?: string;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  testId?: string;
}

/**
 * Start/end calendar-day field backed by a month grid.
 *
 * Not `<input type="date">`: its picker is browser-controlled, cannot be styled to match the
 * design system, and renders differently on every browser — and a range needs one calendar
 * that shows both ends and everything between them, which two native inputs cannot do.
 * Mirrors `DateTimePicker` so the two teach the same interaction.
 *
 * Selection is deliberately two clicks on one grid: the first sets the start and clears the
 * end, the second closes the range. Clicking a day before the current start restarts from
 * there rather than producing a backwards range, so a misclick costs one click to fix.
 */
export function DateRangePicker({
  label,
  value,
  onChange,
  id,
  error,
  disabled = false,
  placeholder = "Select a date range...",
  testId,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const errorId = useId();

  /**
   * Set only when the focused day moved because of something INSIDE the grid — an arrow key,
   * or opening the panel. The month buttons and the presets also move `focusedDay`, and
   * following it from there would drag DOM focus off the button the user is operating: two
   * presses of "Next month" would land the second one on a day cell and silently restart the
   * range. Consumed (and cleared) by the effect below.
   */
  const shouldFocusGridRef = useRef(false);

  const { position: panelPosition, reposition: positionPanel } = usePopoverPosition({
    triggerRef,
    panelRef,
    isOpen,
    width: PANEL_WIDTH,
    heightEstimate: PANEL_HEIGHT_ESTIMATE,
  });

  const today = useMemo(() => todayCalendarDate(), []);

  // The day the grid's roving tabindex sits on, which also decides the visible month.
  const [focusedDay, setFocusedDay] = useState<CalendarDate>(
    () => value.start ?? today
  );

  /**
   * The day under the pointer while an end date is still being chosen, used to shade the
   * range the next click would produce. Purely a preview — it never becomes the value.
   */
  const [hoveredDay, setHoveredDay] = useState<CalendarDate | null>(null);

  // Re-seed each time the panel opens so it never reopens on a stale month, and land
  // keyboard focus on the existing selection.
  useEffect(() => {
    if (isOpen) {
      setFocusedDay(value.start ?? todayCalendarDate());
      setHoveredDay(null);
      shouldFocusGridRef.current = true;
    }
  }, [isOpen, value.start]);

  // Close on outside pointer-down. mousedown rather than click so the panel closes before a
  // click elsewhere can act on a control it was covering.
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // The panel lives in a portal, so it is NOT inside containerRef. Both have to be
      // checked or every click inside the panel would close it.
      if (
        !containerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen]);

  // Move real DOM focus to follow the roving tabindex, but only when the move came from
  // inside the grid. `isPositioned` rather than `panelPosition` itself: the grid only exists
  // once the portalled panel has mounted, but the position object is replaced on every scroll
  // frame, and depending on it would yank focus back into the calendar mid-scroll.
  const isPositioned = panelPosition !== null;
  useEffect(() => {
    if (!isOpen || !isPositioned || !shouldFocusGridRef.current) return;
    shouldFocusGridRef.current = false;
    gridRef.current
      ?.querySelector<HTMLButtonElement>(`[data-day="${dayKey(focusedDay)}"]`)
      // The panel is fixed-position and already on screen; letting the browser scroll to the
      // cell would drag the modal body underneath it.
      ?.focus({ preventScroll: true });
  }, [isOpen, focusedDay, isPositioned]);

  const selectDay = useCallback(
    (day: CalendarDate) => {
      const { start, end } = value;

      // Start a fresh range when there is nothing pending, when both ends are already set,
      // or when the click lands before the pending start (which would otherwise be backwards).
      if (!start || end || compareCalendarDates(day, start) < 0) {
        onChange({ start: day, end: null });
        return;
      }

      onChange({ start, end: day });
      setHoveredDay(null);
    },
    [value, onChange]
  );

  const applyPreset = useCallback(
    (preset: (typeof PRESETS)[number]) => {
      // Recomputed rather than read off `today`: the dashboard is long-lived, and a session
      // left open across midnight would otherwise build every preset off yesterday.
      const range = preset.build(todayCalendarDate());
      onChange(range);
      // Jump the grid to the preset's start so the panel shows what was just chosen rather
      // than whatever month was open. Focus stays on the preset button — see
      // shouldFocusGridRef — so the user can tab across presets and compare them.
      if (range.start) setFocusedDay(range.start);
      setHoveredDay(null);
    },
    [onChange]
  );

  const handleGridKeyDown = (event: React.KeyboardEvent) => {
    const moves: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };

    // Every branch below originates inside the grid, so DOM focus should follow the roving
    // tabindex. (The month buttons and presets move the same state from outside it and
    // deliberately do not set this.)
    shouldFocusGridRef.current = true;

    if (event.key in moves) {
      event.preventDefault();
      setFocusedDay((current) => addCalendarDays(current, moves[event.key]));
      return;
    }

    switch (event.key) {
      case "Home":
        event.preventDefault();
        setFocusedDay((current) =>
          addCalendarDays(
            current,
            -new Date(current.year, current.month - 1, current.day).getDay()
          )
        );
        return;
      case "End":
        event.preventDefault();
        setFocusedDay((current) =>
          addCalendarDays(
            current,
            6 - new Date(current.year, current.month - 1, current.day).getDay()
          )
        );
        return;
      case "PageUp":
        event.preventDefault();
        setFocusedDay((current) => addCalendarMonths(current, -1));
        return;
      case "PageDown":
        event.preventDefault();
        setFocusedDay((current) => addCalendarMonths(current, 1));
        return;
      case "Enter":
      case " ":
        event.preventDefault();
        selectDay(focusedDay);
        return;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        return;
      default:
    }
  };

  // Six weeks starting from the Sunday on or before the 1st, so the grid height never
  // changes as the user pages through months. Grouped into weeks because `role="grid"`
  // requires rows — a screen reader cannot announce a cell's position without them.
  const gridWeeks = useMemo(() => {
    const lead = firstWeekdayOfCalendarMonth(focusedDay.year, focusedDay.month);
    const first: CalendarDate = { ...focusedDay, day: 1 };
    const days = Array.from({ length: 42 }, (_, index) =>
      addCalendarDays(first, index - lead)
    );
    return Array.from({ length: 6 }, (_, week) => days.slice(week * 7, week * 7 + 7));
  }, [focusedDay]);

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(
        new Date(focusedDay.year, focusedDay.month - 1, 1)
      ),
    [focusedDay.year, focusedDay.month]
  );

  // While only a start is set, shade against the hovered day so the user can see the range
  // they are about to commit to. Suppressed when the pointer is BEFORE the start, because
  // clicking there restarts the range rather than closing it backwards — shading it would
  // promise a range the click will not produce.
  const previewEnd =
    value.end ??
    (value.start && hoveredDay && compareCalendarDates(hoveredDay, value.start) >= 0
      ? hoveredDay
      : null);

  const triggerLabel = value.start ? formatCalendarRange(value) : placeholder;

  return (
    <div className="date-range-picker" ref={containerRef}>
      <label className="date-range-picker__label" htmlFor={id}>
        {label}
      </label>

      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`date-range-picker__trigger${
          error ? " date-range-picker__trigger--error" : ""
        }${value.start ? "" : " date-range-picker__trigger--placeholder"}`}
        onClick={() => {
          if (!isOpen) positionPanel();
          setIsOpen((open) => !open);
        }}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        // Without these, someone who tabs back to a rejected field hears only the range and
        // has no way to learn why the Export button is disabled.
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        data-testid={testId}
      >
        {triggerLabel}
      </button>

      {isOpen &&
        panelPosition &&
        createPortal(
          <div
            className="date-range-picker__panel"
            ref={panelRef}
            // Marks the portalled panel as belonging to whatever field opened it. A dialog's
            // focus trap needs to treat it as inside, and it is not a DOM descendant.
            data-picker-panel=""
            role="dialog"
            aria-label={`Choose ${label.toLowerCase()}`}
            style={{ top: panelPosition.top, left: panelPosition.left }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                setIsOpen(false);
                triggerRef.current?.focus();
              }
            }}
          >
            <div className="date-range-picker__presets" role="group" aria-label="Quick ranges">
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  className="date-range-picker__preset"
                  data-testid={
                    testId
                      ? `${testId}-preset-${preset.label.toLowerCase().replace(/\s+/g, "-")}`
                      : undefined
                  }
                  onClick={() => applyPreset(preset)}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="date-range-picker__month-nav">
              <button
                type="button"
                className="date-range-picker__month-button"
                onClick={() => setFocusedDay((current) => addCalendarMonths(current, -1))}
                aria-label="Previous month"
                data-testid={testId ? `${testId}-prev-month` : undefined}
              >
                &#8249;
              </button>
              <span
                className="date-range-picker__month-label"
                aria-live="polite"
                data-testid={testId ? `${testId}-month` : undefined}
              >
                {monthLabel}
              </span>
              <button
                type="button"
                className="date-range-picker__month-button"
                onClick={() => setFocusedDay((current) => addCalendarMonths(current, 1))}
                aria-label="Next month"
                data-testid={testId ? `${testId}-next-month` : undefined}
              >
                &#8250;
              </button>
            </div>

            <div
              className="date-range-picker__grid"
              role="grid"
              aria-label={monthLabel}
              ref={gridRef}
              onKeyDown={handleGridKeyDown}
              onMouseLeave={() => setHoveredDay(null)}
            >
              {/* A real header row rather than an aria-hidden strip: without column headers
                  a screen-reader user has no way to tell which weekday a day falls on. */}
              <div className="date-range-picker__weekdays" role="row">
                {WEEKDAY_LABELS.map((weekday) => (
                  <span
                    key={weekday}
                    role="columnheader"
                    className="date-range-picker__weekday"
                  >
                    {weekday}
                  </span>
                ))}
              </div>

              {gridWeeks.map((week) => (
                <div key={dayKey(week[0])} className="date-range-picker__week" role="row">
                  {week.map((day) => {
                    const key = dayKey(day);
                    const isEndpoint =
                      isSameCalendarDate(day, value.start) ||
                      isSameCalendarDate(day, value.end);
                    const inRange = isWithinCalendarRange(day, value.start, previewEnd);
                    const classes = [
                      "date-range-picker__day",
                      day.month === focusedDay.month
                        ? ""
                        : "date-range-picker__day--outside",
                      inRange && !isEndpoint ? "date-range-picker__day--in-range" : "",
                      isEndpoint ? "date-range-picker__day--selected" : "",
                      isSameCalendarDate(day, today) ? "date-range-picker__day--today" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");

                    return (
                      // gridcell on the wrapper, not the button: putting it on the button
                      // would override its implicit role, so nothing would announce the day
                      // as activatable.
                      <div key={key} role="gridcell" aria-selected={isEndpoint}>
                        <button
                          type="button"
                          data-day={key}
                          data-testid={testId ? `${testId}-day-${key}` : undefined}
                          className={classes}
                          tabIndex={isSameCalendarDate(day, focusedDay) ? 0 : -1}
                          // The bare number leaves a screen-reader user arrowing from
                          // "31" to "1" with no idea the month changed.
                          aria-label={formatCalendarDate(day)}
                          onClick={() => {
                            setFocusedDay(day);
                            selectDay(day);
                          }}
                          // Only while an end date is pending — otherwise every pointer
                          // move across the grid re-renders the panel for no visual change.
                          onMouseEnter={
                            value.start && !value.end
                              ? () => setHoveredDay(day)
                              : undefined
                          }
                        >
                          {day.day}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="date-range-picker__footer">
              <span className="date-range-picker__summary" aria-live="polite">
                {value.start && !value.end
                  ? "Pick an end date"
                  : formatCalendarRange(value) || "No range selected"}
              </span>
              {/* Explicit commit. Clicking away also keeps the selection, but silently —
                  which reads as "that did not take". */}
              <button
                type="button"
                className="date-range-picker__done-button"
                onClick={() => {
                  setIsOpen(false);
                  triggerRef.current?.focus();
                }}
                data-testid={testId ? `${testId}-done` : undefined}
              >
                Done
              </button>
            </div>
          </div>,
          document.body
        )}

      {error && (
        <span className="date-range-picker__error" role="alert" id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
}
